import axios from 'axios';

const API_BASE_URL = 'https://api.flatexpense.com/api';

// DEBUG: Set to true to use mock data when backend is not running
// export const USE_MOCK_DATA = true;
export const USE_MOCK_DATA = false;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' }
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (data) => api.post('/auth/login', data);
export const register = (data) => api.post('/auth/register', data);
export const registerWithApartment = (data) => api.post('/auth/register-with-apartment', data);
export const forgotPassword = (data) => api.post('/auth/forgot-password', data);
export const resetPassword = (token, newPassword) => api.post('/auth/reset-password', { token, newPassword });

// Apartments
export const getApartments = () => api.get('/apartments');
export const getApartment = (id) => api.get(`/apartments/${id}`);
export const createApartment = (data) => {
  const formData = new FormData();
  formData.append('name', data.name);
  formData.append('address', data.address);
  formData.append('totalFlats', data.totalFlats);
  if (data.associationName) formData.append('associationName', data.associationName);
  if (data.bankName) formData.append('bankName', data.bankName);
  if (data.bankAccountNumber) formData.append('bankAccountNumber', data.bankAccountNumber);
  if (data.bankIfscCode) formData.append('bankIfscCode', data.bankIfscCode);
  if (data.bankBranch) formData.append('bankBranch', data.bankBranch);
  if (data.upiId) formData.append('upiId', data.upiId);
  formData.append('openingFundBalance', (data.openingFundBalance || 0).toString());
  if (data.logoImage) formData.append('logoImage', data.logoImage);
  if (data.paymentQrImage) formData.append('paymentQrImage', data.paymentQrImage);
  return api.post('/apartments', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const updateApartment = (id, data) => {
  const formData = new FormData();
  formData.append('name', data.name);
  formData.append('address', data.address);
  formData.append('totalFlats', data.totalFlats);
  if (data.associationName) formData.append('associationName', data.associationName);
  if (data.bankName) formData.append('bankName', data.bankName);
  if (data.bankAccountNumber) formData.append('bankAccountNumber', data.bankAccountNumber);
  if (data.bankIfscCode) formData.append('bankIfscCode', data.bankIfscCode);
  if (data.bankBranch) formData.append('bankBranch', data.bankBranch);
  if (data.upiId) formData.append('upiId', data.upiId);
  formData.append('openingFundBalance', (data.openingFundBalance || 0).toString());
  if (data.logoImage) formData.append('logoImage', data.logoImage);
  if (data.paymentQrImage) formData.append('paymentQrImage', data.paymentQrImage);
  return api.put(`/apartments/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const deleteApartment = (id) => api.delete(`/apartments/${id}`);
export const updateApartmentPlan = (id, planType, planExpiryDate) => api.put(`/apartments/${id}/plan`, { planType, planExpiryDate });

// Apartment Users
export const getApartmentUsers = (apartmentId) => api.get(`/apartments/${apartmentId}/users`);
export const addUserToApartment = (apartmentId, data) => api.post(`/apartments/${apartmentId}/users`, data);
export const createAndAddUser = (apartmentId, data) => api.post(`/apartments/${apartmentId}/users/create`, data);
export const updateApartmentUser = (apartmentId, id, data) => api.put(`/apartments/${apartmentId}/users/${id}`, data);
export const removeUserFromApartment = (apartmentId, id) => api.delete(`/apartments/${apartmentId}/users/${id}`);

// Expense Categories
export const getExpenseCategories = (apartmentId) => api.get(`/expensecategories/apartment/${apartmentId}`);
export const createExpenseCategory = (data) => api.post('/expensecategories', data);
export const updateExpenseCategory = (id, data) => api.put(`/expensecategories/${id}`, data);
export const deleteExpenseCategory = (id) => api.delete(`/expensecategories/${id}`);

// Expenses
export const getExpenses = (apartmentId, month, year) => api.get(`/expenses/apartment/${apartmentId}`, { params: { month, year } });
export const createExpense = (data) => {
  const formData = new FormData();
  formData.append('apartmentId', data.apartmentId.toString());
  formData.append('categoryId', data.categoryId.toString());
  formData.append('description', data.description);
  formData.append('amount', data.amount.toString());
  formData.append('expenseDate', data.expenseDate);
  if (data.billImage) {
    formData.append('billImage', data.billImage);
  }
  return api.post('/expenses', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const createPerFlatExpense = (data) => api.post('/expenses/per-flat', data);
export const updateExpense = (id, data) => {
  const formData = new FormData();
  formData.append('categoryId', data.categoryId.toString());
  formData.append('description', data.description);
  formData.append('amount', data.amount.toString());
  formData.append('expenseDate', data.expenseDate);
  if (data.billImage) {
    formData.append('billImage', data.billImage);
  }
  return api.put(`/expenses/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const deleteExpense = (id) => api.delete(`/expenses/${id}`);
export const approveExpense = (id, approved, rejectionReason) => api.put(`/expenses/${id}/approve`, { approved, rejectionReason });
export const getExpenseBillUrl = (id) => `${API_BASE_URL}/expenses/${id}/bill`;
export const getMonthLockStatus = (apartmentId, month, year) => api.get(`/expenses/apartment/${apartmentId}/lock-status`, { params: { month, year } });
export const toggleMonthLock = (apartmentId, month, year, isLocked) => api.post(`/expenses/apartment/${apartmentId}/lock`, { month, year, isLocked });

// Contributions
export const getContributions = (apartmentId, month, year) => api.get(`/contributions/apartment/${apartmentId}`, { params: { month, year } });
export const createContribution = (data) => api.post('/contributions', data);
export const deleteContribution = (id) => api.delete(`/contributions/${id}`);

// Dashboard
export const getMonthlyDashboard = (apartmentId, month, year) => api.get(`/dashboard/apartment/${apartmentId}/monthly`, { params: { month, year } });
export const getDateRangeDashboard = (apartmentId, fromDate, toDate) => api.get(`/dashboard/apartment/${apartmentId}/daterange`, { params: { fromDate, toDate } });

// Reports
export const downloadExcelReport = (apartmentId, month, year) => api.get(`/reports/apartment/${apartmentId}/excel`, { params: { month, year }, responseType: 'blob' });

// Audit Logs
export const getAuditLogs = (apartmentId, params) => api.get(`/auditlogs/apartment/${apartmentId}`, { params });

// Notifications
export const getNotifications = (unreadOnly = false) => api.get('/notifications', { params: { unreadOnly } });
export const getUnreadCount = () => api.get('/notifications/unread-count');
export const markNotificationRead = (id) => api.put(`/notifications/${id}/read`);
export const markAllNotificationsRead = () => api.put('/notifications/read-all');

// Profile
export const getProfile = () => api.get('/profile');
export const updateProfile = (data) => api.put('/profile', data);
export const changePassword = (data) => api.put('/profile/change-password', data);

// Function Hall Bookings
export const getFunctionHallBookings = (apartmentId, month, year) => api.get(`/functionhall/apartment/${apartmentId}`, { params: { month, year } });
export const checkHallAvailability = (apartmentId, date) => api.get(`/functionhall/apartment/${apartmentId}/availability`, { params: { date } });
export const createFunctionHallBooking = (data) => api.post('/functionhall', data);
export const updateFunctionHallBooking = (id, data) => api.put(`/functionhall/${id}`, data);
export const deleteFunctionHallBooking = (id) => api.delete(`/functionhall/${id}`);

// Corpus Fund
export const getCorpusFundSummary = (apartmentId) => api.get(`/corpusfund/apartment/${apartmentId}/summary`);
export const getCorpusContributions = (apartmentId) => api.get(`/corpusfund/contributions/apartment/${apartmentId}`);
export const createCorpusContribution = (data) => api.post('/corpusfund/contributions', data);
export const deleteCorpusContribution = (id) => api.delete(`/corpusfund/contributions/${id}`);
export const getCorpusExpenses = (apartmentId) => api.get(`/corpusfund/expenses/apartment/${apartmentId}`);
export const createCorpusExpense = (data) => {
  const formData = new FormData();
  formData.append('apartmentId', data.apartmentId);
  formData.append('description', data.description);
  formData.append('amount', data.amount);
  formData.append('expenseDate', data.expenseDate);
  if (data.notes) formData.append('notes', data.notes);
  if (data.billImage) formData.append('billImage', data.billImage);
  return api.post('/corpusfund/expenses', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const updateCorpusExpense = (id, data) => api.put(`/corpusfund/expenses/${id}`, data);
export const deleteCorpusExpense = (id) => api.delete(`/corpusfund/expenses/${id}`);
export const updateCorpusContribution = (id, data) => api.put(`/corpusfund/contributions/${id}`, data);

export default api;

// Festivals
export const getFestivals = (apartmentId) => api.get(`/festivals/apartment/${apartmentId}`);
export const createFestival = (data) => api.post('/festivals', data);
export const deleteFestival = (id) => api.delete(`/festivals/${id}`);
export const getFestivalContributions = (festivalId) => api.get(`/festivals/${festivalId}/contributions`);
export const createFestivalContribution = (data) => api.post('/festivals/contributions', data);
export const deleteFestivalContribution = (id) => api.delete(`/festivals/contributions/${id}`);
export const getFestivalExpenses = (festivalId) => api.get(`/festivals/${festivalId}/expenses`);
export const createFestivalExpense = (data) => {
  const formData = new FormData();
  formData.append('festivalId', data.festivalId.toString());
  formData.append('description', data.description);
  formData.append('amount', data.amount.toString());
  formData.append('expenseDate', data.expenseDate);
  if (data.notes) formData.append('notes', data.notes);
  if (data.billImage) formData.append('billImage', data.billImage);
  return api.post('/festivals/expenses', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const deleteFestivalExpense = (id) => api.delete(`/festivals/expenses/${id}`);
export const exportFestival = (festivalId) => api.get(`/festivals/${festivalId}/export`, { responseType: 'blob' });
